import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import "../Styles/MediaTV.css";

const API_BASE_URL = "/api/media-tv";

const INITIAL_FORM_STATE = {
  title: "",
  channelName: "",
  description: "",
  mediaDate: "",
  active: true
};

function MediaTV({ adminMode = false, refreshDashboard }) {

  const [mediaList, setMediaList] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);

  const [formData, setFormData] = useState(INITIAL_FORM_STATE);

  // Uploaded video file
  const [file, setFile] = useState(null);

  // Local video preview
  const [videoPreviewUrl, setVideoPreviewUrl] = useState("");

  const [uploadProgress, setUploadProgress] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);


  /* =========================================================
     LOAD MEDIA TV
  ========================================================= */

  const loadMediaTV = useCallback(async () => {

    setLoading(true);
    setError(null);

    try {

      const url = adminMode
        ? `${API_BASE_URL}/all`
        : API_BASE_URL;

      const response = await axios.get(url);

      setMediaList(response.data || []);

    } catch (err) {

      console.error("Media TV fetch error:", err);

      setError(
        "Failed to load Media TV records. Please try again."
      );

    } finally {

      setLoading(false);
    }

  }, [adminMode]);


  useEffect(() => {

    loadMediaTV();

  }, [loadMediaTV]);


  /* =========================================================
     FORM HANDLER
  ========================================================= */

  const handleChange = (e) => {

    const {
      name,
      value,
      type,
      checked
    } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : value
    }));
  };


  /* =========================================================
     FILE CHANGE
  ========================================================= */

  const handleFileChange = (e) => {

    const selectedFile =
      e.target.files[0];

    if (!selectedFile) {
      return;
    }

    if (!selectedFile.type.startsWith("video/")) {

      alert("Please select a valid video file.");

      e.target.value = "";

      return;
    }

    // Remove previous preview
    if (videoPreviewUrl) {

      URL.revokeObjectURL(
        videoPreviewUrl
      );
    }

    setFile(selectedFile);

    const preview =
      URL.createObjectURL(selectedFile);

    setVideoPreviewUrl(preview);
  };


  /* =========================================================
     RESET FORM
  ========================================================= */

  const handleResetForm = () => {

    setShowForm(false);

    setEditId(null);

    setFormData({
      ...INITIAL_FORM_STATE
    });

    setFile(null);

    if (videoPreviewUrl) {

      URL.revokeObjectURL(
        videoPreviewUrl
      );
    }

    setVideoPreviewUrl("");

    setUploadProgress(0);
  };


  /* =========================================================
     ADD NEW
  ========================================================= */

  const handleAddNew = () => {

    handleResetForm();

    setShowForm(true);
  };


  /* =========================================================
     EDIT
  ========================================================= */

  const handleEdit = (media) => {

    setEditId(media.id);

    setFormData({

      title: media.title || "",

      channelName:
        media.channelName || "",

      description:
        media.description || "",

      mediaDate:
        media.mediaDate
          ? media.mediaDate.split("T")[0]
          : "",

      active:
        media.active !== false
    });

    // New file is optional during edit
    setFile(null);

    if (videoPreviewUrl) {

      URL.revokeObjectURL(
        videoPreviewUrl
      );
    }

    setVideoPreviewUrl("");

    setShowForm(true);
  };


  /* =========================================================
     SAVE / UPDATE
  ========================================================= */

  const handleSubmit = async (e) => {

    e.preventDefault();

    if (!formData.title.trim()) {

      alert("Please enter the Media TV title.");

      return;
    }

    if (!formData.channelName.trim()) {

      alert("Please enter the channel name.");

      return;
    }

    // File required only when adding
    if (!editId && !file) {

      alert("Please select a video file.");

      return;
    }

    try {

      setLoading(true);

      setUploadProgress(0);


      /* =====================================================
         FORM DATA
      ===================================================== */

      const payload = new FormData();

      payload.append(
        "title",
        formData.title
      );

      payload.append(
        "channelName",
        formData.channelName
      );

      payload.append(
        "description",
        formData.description
      );

      payload.append(
        "mediaDate",
        formData.mediaDate
      );

 

      /* =====================================================
         ADD VIDEO FILE
      ===================================================== */

      if (file) {

        payload.append(
          "file",
          file
        );
      }


      /* =====================================================
         REQUEST CONFIG
      ===================================================== */

      const config = {

        headers: {
          "Content-Type":
            "multipart/form-data"
        },

        onUploadProgress:
          (progressEvent) => {

            if (
              progressEvent.total &&
              file
            ) {

              const percentCompleted =
                Math.round(
                  (progressEvent.loaded * 100) /
                  progressEvent.total
                );

              setUploadProgress(
                percentCompleted
              );
            }
          }
      };


      /* =====================================================
         UPDATE
      ===================================================== */

      if (editId) {

        await axios.put(
          `${API_BASE_URL}/${editId}`,
          payload,
          config
        );

        alert(
          "Media TV updated successfully."
        );

      }

      /* =====================================================
         ADD
      ===================================================== */

      else {

        await axios.post(
          `${API_BASE_URL}/upload`,
          payload,
          config
        );

        alert(
          "Media TV added successfully."
        );
      }


      /* =====================================================
         REFRESH
      ===================================================== */

      handleResetForm();

      await loadMediaTV();

      if (refreshDashboard) {

        refreshDashboard();
      }

    } catch (err) {

      console.error(
        "Media TV save error:",
        err
      );

      console.error(
        "Server response:",
        err.response?.data
      );

      alert(
        err.response?.data ||
        "Unable to save Media TV record."
      );

    } finally {

      setLoading(false);

      setUploadProgress(0);
    }
  };


  /* =========================================================
     DELETE
  ========================================================= */

  const handleDelete = async (id) => {

    if (
      !window.confirm(
        "Are you sure you want to delete this Media TV record?"
      )
    ) {
      return;
    }

    try {

      await axios.delete(
        `${API_BASE_URL}/${id}`
      );

      alert(
        "Media TV deleted successfully."
      );

      await loadMediaTV();

      if (refreshDashboard) {

        refreshDashboard();
      }

    } catch (err) {

      console.error(
        "Media TV delete error:",
        err
      );

      alert(
        "Unable to delete Media TV record."
      );
    }
  };


  /* =========================================================
     FORMAT DATE
  ========================================================= */

  const formatDate = (dateStr) => {

    if (!dateStr) {
      return "-";
    }

    const date =
      new Date(dateStr);

    return isNaN(date.getTime())
      ? "-"
      : date.toLocaleDateString("en-IN");
  };


  /* =========================================================
     ADMIN MODE
  ========================================================= */

  if (adminMode) {

    return (

      <div className="media-tv-admin">

        <header className="media-tv-admin-header">

          <div>

            <h2>
              📺 Media TV Management
            </h2>

            <p className="sub-text">
              Manage temple broadcasts and news coverage
            </p>

          </div>

          <div className="media-tv-admin-actions">

            <button
              type="button"
              className="btn btn-secondary"
              onClick={loadMediaTV}
              disabled={loading}
            >
              🔄 Refresh
            </button>

            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAddNew}
            >
              ➕ Add New Record
            </button>

          </div>

        </header>


        {/* ===================================================
            FORM
        =================================================== */}

        {showForm && (

          <div className="media-tv-form-card">

            <h3>
              {editId
                ? "Edit Media TV Record"
                : "Add New Media TV Record"}
            </h3>


            <form
              onSubmit={handleSubmit}
              className="media-tv-form"
            >


              {/* TITLE + CHANNEL */}

              <div className="form-grid-2">

                <div className="form-group">

                  <label htmlFor="title">
                    Title *
                  </label>

                  <input
                    id="title"
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Enter media title"
                    required
                  />

                </div>


                <div className="form-group">

                  <label htmlFor="channelName">
                    Channel Name *
                  </label>

                  <input
                    id="channelName"
                    type="text"
                    name="channelName"
                    value={formData.channelName}
                    onChange={handleChange}
                    placeholder="Enter channel name"
                    required
                  />

                </div>

              </div>


              {/* DESCRIPTION */}

              <div className="form-group">

                <label htmlFor="description">
                  Description
                </label>

                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter brief summary or video details"
                  rows="3"
                />

              </div>


              {/* VIDEO FILE */}

              <div className="form-group">

                <label htmlFor="mediaVideoFile">
                  {editId
                    ? "Replace Video (Optional)"
                    : "Upload Video File *"}
                </label>

                <div className="file-upload-box">

                  <input
                    id="mediaVideoFile"
                    type="file"
                    accept="video/*"
                    onChange={handleFileChange}
                    className="file-input"
                  />

                  <div className="upload-hint">

                    📁{" "}

                    {file
                      ? file.name
                      : editId
                      ? "Choose a new video only if you want to replace the existing video"
                      : "Choose MP4/WebM video file"}

                  </div>

                </div>

              </div>


              {/* VIDEO PREVIEW */}

              {videoPreviewUrl && (

                <div className="video-preview-wrapper">

                  <p className="preview-label">
                    Selected Video Preview:
                  </p>

                  <video
                    src={videoPreviewUrl}
                    controls
                    className="video-preview"
                  />

                </div>

              )}


              {/* UPLOAD PROGRESS */}

              {uploadProgress > 0 &&
                uploadProgress < 100 && (

                  <div className="progress-bar-container">

                    <div
                      className="progress-bar"
                      style={{
                        width:
                          `${uploadProgress}%`
                      }}
                    >
                      {uploadProgress}%
                    </div>

                  </div>
                )}


              {/* DATE + ACTIVE */}

              <div className="form-grid-2 align-center">

                <div className="form-group">

                  <label htmlFor="mediaDate">
                    Media Date
                  </label>

                  <input
                    id="mediaDate"
                    type="date"
                    name="mediaDate"
                    value={formData.mediaDate}
                    onChange={handleChange}
                  />

                </div>


                <div className="form-group checkbox-group">

                  <label className="checkbox-label">

                    <input
                      type="checkbox"
                      name="active"
                      checked={formData.active}
                      onChange={handleChange}
                    />

                    <span>
                      Active Status
                    </span>

                  </label>

                </div>

              </div>


              {/* BUTTONS */}

              <div className="form-actions">

                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={loading}
                >

                  {loading
                    ? "⏳ Uploading..."
                    : editId
                    ? "✏️ Update Record"
                    : "💾 Save Record"}

                </button>


                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={handleResetForm}
                  disabled={loading}
                >
                  ❌ Cancel
                </button>

              </div>

            </form>

          </div>
        )}


        {/* ===================================================
            RECORDS
        =================================================== */}

        <section className="media-tv-records">

          <h3>
            Existing Records ({mediaList.length})
          </h3>


          {loading && !showForm ? (

            <div className="media-tv-status">
              Loading records...
            </div>

          ) : error ? (

            <div className="media-tv-status error">
              {error}
            </div>

          ) : mediaList.length === 0 ? (

            <div className="media-tv-empty">

              <p>
                No Media TV records available.
              </p>

            </div>

          ) : (

            <div className="table-responsive">

              <table className="media-tv-table">

                <thead>

                  <tr>

                    <th>ID</th>
                    <th>Title</th>
                    <th>Channel</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th className="text-right">
                      Actions
                    </th>

                  </tr>

                </thead>


                <tbody>

                  {mediaList.map((media) => (

                    <tr key={media.id}>

                      <td className="cell-id">
                        #{media.id}
                      </td>

                      <td className="cell-title">
                        {media.title}
                      </td>

                      <td>
                        {media.channelName}
                      </td>

                      <td>
                        {formatDate(
                          media.mediaDate
                        )}
                      </td>

                      <td>

                        <span
                          className={
                            `badge ${
                              media.active
                                ? "badge-active"
                                : "badge-inactive"
                            }`
                          }
                        >
                          {media.active
                            ? "Active"
                            : "Inactive"}
                        </span>

                      </td>

                      <td className="text-right cell-actions">

                        <button
                          type="button"
                          className="btn-icon"
                          title="Edit"
                          onClick={() =>
                            handleEdit(media)
                          }
                        >
                          ✏️
                        </button>

                        <button
                          type="button"
                          className="btn-icon btn-icon-danger"
                          title="Delete"
                          onClick={() =>
                            handleDelete(media.id)
                          }
                        >
                          🗑️
                        </button>

                      </td>

                    </tr>

                  ))}

                </tbody>

              </table>

            </div>
          )}

        </section>

      </div>
    );
  }


  /* =========================================================
     PUBLIC MODE
  ========================================================= */

  return (

    <div className="media-tv-public">

      <div className="media-tv-container">

        {loading ? (

          <div className="media-tv-status">
            Loading coverage...
          </div>

        ) : error ? (

          <div className="media-tv-status error">
            {error}
          </div>

        ) : mediaList.length === 0 ? (

          <div className="media-tv-empty">

            <p>
              No Media TV coverage available at this time.
            </p>

          </div>

        ) : (

          <div className="media-tv-grid">

            {mediaList.map((media) => (

              <article
                className="media-card"
                key={media.id}
              >

                {/* VIDEO */}

                <div className="media-card-media">

                  {media.fileUrl ? (
  <video
    controls
    preload="metadata"
    className="media-tv-video"
    playsInline
  >
    <source
      src={`${media.fileUrl}`}
      type={media.fileType || "video/mp4"}
    />
    Your browser does not support the video element.
  </video>
) : (

                    <div className="media-card-placeholder">
                      📺
                    </div>

                  )}

                </div>


                {/* CONTENT */}

                <div className="media-card-body">

                  <div className="media-card-meta">

                    <span className="media-channel">
                      📺 {media.channelName}
                    </span>

                    {media.mediaDate && (

                      <span className="media-date">

                        📅{" "}
                        {formatDate(
                          media.mediaDate
                        )}

                      </span>

                    )}

                  </div>


                  <h3 className="media-card-title">
                    {media.title}
                  </h3>


                  {media.description && (

                    <p className="media-card-description">
                      {media.description}
                    </p>

                  )}

                </div>

              </article>

            ))}

          </div>
        )}

      </div>

    </div>
  );
}

export default MediaTV;